import dayjs from 'dayjs';
import { Duration } from 'dayjs/plugin/duration.js';
import { TimeZoneRegion } from './serializable/time-zone-region.js';
import { TimeZone } from '../time/timezone.js';
import { TrueTime } from '../time/true-time.js';
import { DayOfYear } from './serializable/day-of-year.js';
import { StaffProfile } from './serializable/staff-profile.js';
import { TimeOfDay } from './serializable/time-of-day.js';
import { BusinessModuleRef } from './serializable/business-module-ref.js';
import { ComponentList } from '../components/core/component-list.js';
import { IReservationWithDate, IReservationWithGroup, Reservation } from './reservation.js';
import { QueryableOptionalString } from './core/optional.js';
import { fromEnumAsString } from '../serialization/enum-utils.js';

export enum CalendarEntryStatus {
  Open = 'OPEN',
  Aborted = 'ABORTED',
  Canceled = 'CANCELED',
  Rescheduled = 'RESCHEDULED',
}

export abstract class CalendarEntry extends Reservation implements IReservationWithGroup, IReservationWithDate {
  abstract get uniqueId(): string;
  abstract get status(): CalendarEntryStatus;
  abstract get creatorId(): string;
  abstract get businessRef(): BusinessModuleRef;
  abstract get groupId(): string | null;
  abstract get parentId(): QueryableOptionalString;
  abstract get dayId(): string;
  abstract get monthId(): string;
  abstract get dayOfYear(): DayOfYear;
  abstract get timeOfDay(): TimeOfDay;
  abstract get creationDate(): dayjs.Dayjs;
  abstract get timezone(): TimeZoneRegion;
  abstract get staffMemberId(): QueryableOptionalString;
  abstract get staffMember(): StaffProfile | null;
  abstract get components(): ComponentList;

  abstract get duration(): Duration;

  get isOpen(): boolean {
    return this.status === CalendarEntryStatus.Open;
  }

  get isCanceled(): boolean {
    return this.status === CalendarEntryStatus.Canceled;
  }

  get date(): dayjs.Dayjs {
    return this.dayOfYear.toDate(TimeZone.ofRegion(this.timezone));
  }

  get time(): dayjs.Dayjs {
    return this.dayOfYear.toDateAndTime(this.timeOfDay, TimeZone.ofRegion(this.timezone));
  }

  get isFinished(): boolean {
    const startDate = this.time;
    const endDate = startDate.add(this.duration);

    return endDate.isBefore(TrueTime.now());
  }

  get isUpcoming(): boolean {
    const startDate = this.time;
    return startDate.isAfter(TrueTime.now());
  }

  get isInProgress(): boolean {
    const now = TrueTime.now();
    const startDate = this.time;
    const endDate = startDate.add(this.duration);

    return (now.isSame(startDate) || now.isAfter(startDate)) && now.isBefore(endDate);
  }

  static encodedStatusSafe(status: CalendarEntryStatus): string {
    return fromEnumAsString(status);
  }
}
